import React from 'react';
import { Box, keyframes } from '@mui/material';

interface AILoadingIconProps {
  id?: string;
  size?: number;
  sx?: any;
}

// Star blinking animation
const starBlink = keyframes`
  0%, 100% {
    opacity: 0.4;
    transform: scale(0.9);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;

// Dot blinking animation
const dotBlink = keyframes`
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
`;

const StarSVG = () => (
  <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M9 19.5C8.63543 19.5 8.3233 19.2694 8.24339 18.9398L7.85634 17.342C7.1272 14.3264 5.05464 11.9447 2.44771 11.1263L1.02688 10.679C0.71225 10.5821 0.5 10.3078 0.5 9.99885C0.5 9.6899 0.71225 9.41785 1.02938 9.31871L2.44771 8.87374C5.05464 8.05527 7.1272 5.67364 7.85634 2.65799L8.24339 1.06025C8.3233 0.730555 8.63543 0.5 9 0.5H9.0025C9.36957 0.5 9.6792 0.73286 9.75911 1.06486L10.1437 2.65568C10.8728 5.67134 12.9454 8.05297 15.5523 8.87143L16.9731 9.31871C17.2877 9.41785 17.5 9.6899 17.5 9.99885C17.5 10.3078 17.2877 10.5798 16.9731 10.679L15.5523 11.1263C12.9454 11.9447 10.8728 14.3264 10.1437 17.342L9.75661 18.9374C9.6792 19.2648 9.36957 19.4977 9.0025 19.4977H9V19.5Z" 
      fill="url(#paint0_linear_14153_40110)"
    />
    <defs>
      <linearGradient 
        id="paint0_linear_14153_40110" 
        x1="21.5" 
        y1="21.4487" 
        x2="0.612927" 
        y2="4.53559" 
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0.15" stopColor="#C63AED" />
        <stop offset="0.5" stopColor="#F53FFF" />
        <stop offset="0.84803" stopColor="#FF6B2B" />
      </linearGradient>
    </defs>
  </svg>
);

const FaAILoadingIcon = (props: AILoadingIconProps) => {
  const { id, size = 35, sx } = props;

  const containerSx = {
    position: 'relative',
    width: `${size}px`,
    height: `${size}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...sx
  };

  const starSx = {
    position: 'absolute',
    animation: `${starBlink} 1.8s ease-in-out infinite`,
    top: `${(size * 8) / 35}px`,
    '& svg': {
      width: `${(size * 16) / 35}px`,
      height: `${(size * 18) / 35}px`,
    }
  };

  const baseDotSx = {
    position: 'absolute',
    width: `${(size * 2) / 35}px`,
    height: `${(size * 2) / 35}px`,
    background: 'linear-gradient(135deg, #C63AED 0%, #F53FFF 50%, #FF6B2B 100%)',
    borderRadius: '50%',
    animation: `${dotBlink} 1.5s ease-in-out infinite`,
  };

  const dot1Sx = {
    ...baseDotSx,
    top: `${(size * 10) / 35}px`,
    left: `${(size * 9) / 35}px`,
    animationDelay: '0s',
  };

  const dot2Sx = {
    ...baseDotSx,
    top: `${(size * 10) / 35}px`,
    right: `${(size * 8) / 35}px`,
    animationDelay: '0.5s',
  };

  const dot3Sx = {
    ...baseDotSx,
    bottom: `${(size * 9) / 35}px`,
    left: `${(size * 10) / 35}px`,
    animationDelay: '1s',
  };

  return (
    <Box id={id ?? ''} sx={containerSx}>
      <Box sx={starSx}>
        <StarSVG />
      </Box>
      <Box sx={dot1Sx} />
      <Box sx={dot2Sx} />
      <Box sx={dot3Sx} />
    </Box>
  );
};

export default FaAILoadingIcon;